/**
 * ADR-0025: one live connection per device to /api/events/stream (server-sent
 * events over fetch, so the access token goes in a header). Events are handed
 * to listeners: open apps (filtered by what each may hear), Space widgets and
 * the installer. The stream resumes after the last event it saw, so nothing is
 * missed across reconnects, token renewals or the app being in the background.
 */
import type { OmniEvent } from "@omni/sdk/protocol";

import { apiBase, getTokens, refreshTokens } from "./api";
import { load, save } from "./storage";

type Listener = (event: OmniEvent) => void;

const listeners = new Set<Listener>();
let running = false;
let controller: AbortController | null = null;
let backoff = 1000;
let connected = false;
const statusListeners = new Set<(connected: boolean) => void>();

const lastKey = () => `omni.events.last:${apiBase()}`;

export function onEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function onEventsStatus(fn: (connected: boolean) => void): () => void {
  statusListeners.add(fn);
  fn(connected);
  return () => statusListeners.delete(fn);
}

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  statusListeners.forEach((fn) => fn(value));
}

function dispatch(event: OmniEvent) {
  save(lastKey(), event.id);
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch (e) {
      console.warn("Event listener failed", e);
    }
  });
}

async function connectOnce(signal: AbortSignal): Promise<void> {
  const tokens = getTokens();
  if (!tokens) throw new Error("signed out");
  const last = load<number>(lastKey(), 0);
  const url = `${apiBase()}/api/events/stream${last ? `?after=${last}` : ""}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tokens.access_token}`, Accept: "text/event-stream" }, signal, cache: "no-store" });
  if (res.status === 401) {
    if (!(await refreshTokens())) throw new Error("signed out");
    return; // reconnect straight away with the new token
  }
  if (!res.ok || !res.body) throw new Error(`events: HTTP ${res.status}`);
  setConnected(true);
  backoff = 1000;
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return; // the server ends the stream when the token expires
    buffer += value;
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      let name = "message";
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) name = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (!data.length) continue;
      try {
        const payload = JSON.parse(data.join("\n"));
        if (name === "omni") dispatch(payload as OmniEvent);
        else if (name === "hello" && !load<number>(lastKey(), 0)) save(lastKey(), payload.latest); // first connect: start from now
      } catch {
        /* malformed block */
      }
    }
  }
}

async function loop() {
  while (running) {
    controller = new AbortController();
    try {
      await connectOnce(controller.signal);
      await new Promise((r) => setTimeout(r, 300));
    } catch (e) {
      if (!running || (e as Error).name === "AbortError") break;
      if ((e as Error).message === "signed out") {
        running = false;
        break;
      }
      setConnected(false);
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 30_000);
    }
  }
  setConnected(false);
}

/** Start (or keep) the live connection. Idempotent. */
export function startEvents(): void {
  if (running) return;
  running = true;
  backoff = 1000;
  void loop();
}

export function stopEvents(): void {
  running = false;
  controller?.abort();
  controller = null;
  setConnected(false);
}

/** After sleep or backgrounding the socket may be dead: reconnect at once. */
export function nudgeEvents(): void {
  if (!running) return startEvents();
  controller?.abort();
  running = false;
  setTimeout(startEvents, 50);
}

/** Sign-out: stop and forget where this device was in the stream. */
export function resetEvents(): void {
  stopEvents();
  save(lastKey(), null);
}
